import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import { ContractAdjustmentsSection } from "./ContractAdjustmentsSection";
import { ContractBillingChannelSection } from "./ContractBillingChannelSection";
import { ContractEconomicsSection } from "./ContractEconomicsSection";
import { ContractWorkspacePlanSection } from "./ContractWorkspacePlanSection";

// PR B (feat/contracts-billing-workspace-v2) — SHARED CANONICAL BILLING WORKSPACE.
//
// Single implementation for contract-governed billing, keyed by contractId. Used
// by BOTH the contract detail (Economía tab) and any installation linked to a
// contract (which inherits this economics — never duplicates it).
//
// It self-fetches the caller's contract capabilities and the contract economic
// summary (original/effective/currency) so every call site shares one canonical
// source of truth. Components:
//   - ContractWorkspacePlanSection → one row per item; explicit certificationId
//     pairing prevents visual and economic double counting.
//   - ContractAdjustmentsSection → increase/decrease, approve/reject/cancel.
//   - ContractEconomicsSection   → effective = original + approved inc − approved dec.
//
// Call sites pass only { contractId }. Capability gating lives here (not in the
// page) so the same fail-closed rules apply for contract and installation alike.

interface Props {
  contractId: number;
  // BILL-04: optional human customer/contract context shown inside sensitive
  // confirmation summaries. Omitted by call sites without that context so the
  // UI never fabricates client data.
  contextLabel?: string;
}

export function ContractBillingWorkspace({ contractId, contextLabel }: Props) {
  const capsQuery = trpc.customers.contracts.getMyCapabilities.useQuery();
  const caps = capsQuery.data;

  const summaryQuery = trpc.customers.contracts.economySummary.useQuery(
    { contractId },
    { enabled: !!contractId && (caps?.canReadEconomics ?? false) },
  );

  if (capsQuery.isLoading) {
    return <div className="space-y-3"><Skeleton className="h-40 w-full" /></div>;
  }

  const canReadEconomics = caps?.canReadEconomics ?? false;
  const currency = summaryQuery.data?.currency ?? "EUR";

  return (
    <div id="contract-billing-workspace" className="space-y-4">
      <ContractBillingChannelSection
        contractId={contractId}
        canRead={caps?.canRead ?? false}
        canWriteEconomic={caps?.canWriteEconomic ?? false}
      />
      <ContractWorkspacePlanSection
        contractId={contractId}
        currency={currency}
        canWriteEconomic={caps?.canWriteEconomic ?? false}
        effectiveAmount={summaryQuery.data?.effectiveAmount ?? null}
        contextLabel={contextLabel}
      />
      <ContractAdjustmentsSection
        contractId={contractId}
        canWriteEconomic={caps?.canWriteEconomic ?? false}
        currency={currency}
      />
      <ContractEconomicsSection contractId={contractId} canReadEconomics={canReadEconomics} />
    </div>
  );
}
